import React, { useRef } from 'react'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { closeModalWithDelay, openModal } from '../../../redux/slices/modalSlice'
import { useTranslation } from '../../../hooks/useTranslation'
import { ModalType } from '../../../core/enums/Modals'
import Utils from '../../../core/Utils'
import BackArrow from '../../../img/svgComponents/BackArrow'
import { useNavigate } from 'react-router'
import { RootState } from '../../../redux/store'
import { PopoverAnchor } from '../../Header/Header'
import {
	ArrowRightIcon,
	ArrowUpRightIcon,
	CashbackIcon,
	CloseIcon,
	GiftIcon,
	InfoIcon,
	PlusCircleIcon,
	PointsCoinIcon,
	WalletIcon,
} from '../../../img/svgComponents/UiIcons'

interface BalanceModalProps {
	position?: PopoverAnchor
	closed?: boolean
}

const POPOVER_WIDTH = 360
const EDGE_GAP = 12

const formatMoney = (value: number) => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const BalanceModal = ({ position }: BalanceModalProps) => {
	const dispatch = useAppDispatch()
	const isMobile = Utils.isMobile()
	const navigate = useNavigate()
	const wallet = useAppSelector((state) => state.wallet)
	const profile = useAppSelector((state) => state.userPublic.profile)
	const { modalProps : {isVisible} } = useAppSelector((state: RootState) => state.modal)
	
	const modalRef = useRef<HTMLDivElement>(null)

	// Centre under the balance pill, kept inside the viewport; the arrow follows the pill
	const left = position ? Math.max(EDGE_GAP, Math.min(position.anchorX - POPOVER_WIDTH / 2, window.innerWidth - POPOVER_WIDTH - EDGE_GAP)) : 0
	const modalStyle =
		position && !isMobile
			? ({
					top: position.top,
					left,
					'--arrow-x': `${position.anchorX - left}px`,
			  } as React.CSSProperties)
			: {}

	const { getTexts } = useTranslation(true)

	const handleNavigateBack = () => {
		navigate(-1)
	}

	const handleCashier = (modalProps: { initialTab: string } | null = null) => {
		if (isMobile) {
			navigate({
				pathname: '/cashier',
				search: modalProps?.initialTab === 'withdraw' ? '?type=withdraw' : '',
			})
		} else {
			dispatch(openModal({ modalType: ModalType.Cashier, modalProps }))
		}
	}


	const handleClose = () => {
		dispatch(closeModalWithDelay())
	}


	useOutsideClick(modalRef, handleClose, ModalType.Balance)
	return (
		<div
			className={`wallet-modal ${isVisible || isMobile ? 'animated' : 'close-animated'}`}
			ref={modalRef}
			style={modalStyle}
		>
			<div className="wallet-header">
				{isMobile && (
					<button type="button" className="wallet-header-backIcon" aria-label="Back" onClick={handleNavigateBack}>
						<BackArrow />
					</button>
				)}
				<WalletIcon className="wallet-header-icon" size={28} />
				<h2>Wallet</h2>
				{!isMobile && (
					<button type="button" className="wallet-close" aria-label="Close" onClick={handleClose}>
						<CloseIcon size={22} />
					</button>
				)}
			</div>
			<div className="wallet-cash">
				<div className="wallet-cash-label">Cash balance</div>
				<div className="wallet-cash-value">€ {formatMoney(wallet.balance)}</div>
			</div>
			<div className="wallet-row bonus">
				<GiftIcon className="wallet-row-icon" size={26} />
				<span className="wallet-row-label">{getTexts('bonus_balance')}</span>
				<span className="wallet-row-value">€ {formatMoney(wallet.bonus)}</span>
				<span className="wallet-info" title="Bonus funds can be played on eligible games and become cash once wagering is complete.">
					<InfoIcon size={22} />
				</span>
			</div>
			<div className="wallet-actions">
				<button type="button" className="wallet-action deposit" onClick={() => handleCashier()}>
					<PlusCircleIcon size={24} />
					{getTexts('deposit')}
				</button>
				<button type="button" className="wallet-action withdraw" onClick={() => handleCashier({ initialTab: 'withdraw' })}>
					<ArrowUpRightIcon size={22} />
					{getTexts('withdraw')}
				</button>
			</div>
			<div className="wallet-row loyalty">
				<PointsCoinIcon className="wallet-row-icon" size={28} />
				<span className="wallet-row-label">{getTexts('loyalty_points')}</span>
				<span className="wallet-row-value">{(profile?.ach_points_balance || 0).toLocaleString('en-US')}</span>
			</div>
			<div className="wallet-cashback">
				<CashbackIcon className="cashback-icon" size={30} />
				<div className="cashback-texts">
					<div className="cashback-label">
						<span>Cashback</span>
						<span className="cashback-value">€ 14.00</span>
					</div>
					<div className="cashback-note">Up to 12% cashback on your next deposit.</div>
					<button type="button" className="cashback-link" onClick={() => handleCashier()}>
						Offer details <ArrowRightIcon size={16} />
					</button>
				</div>
			</div>
		</div>
	)
}

export default BalanceModal
